/**
 * sr-shared.ts — the format-agnostic screen-reader helpers shared by BOTH SR
 * surfaces in the product:
 *
 *   1. the shared a11y-view mount's reading-order popout (`renderSrPanel` in
 *      `a11y-view-mount.ts`) — PPTX / DOCX + the PDF `pictures-not-pages` pilot; and
 *   2. the classic pdf.js `/editor`'s `ScreenReaderPanel` (`../editor/screen-reader.ts`)
 *      — the shipped-default PDF path (kept; the all-PNG fork is closed).
 *
 * The two panels are NOT one component — they differ in data model (`TextViewRow`
 * vs `Card`) and feature set (the classic one adds Web-Speech playback + inline alt
 * editing), so unifying the whole surface would be the re-architecture the
 * config-drift RCA explicitly ruled OUT. But the two genuinely *format-agnostic*
 * pieces — reading-order text normalization + the close-button DOM — were duplicated
 * (the trim lived only in `screen-reader.ts`; the shared mount had neither). They are
 * extracted here on the SECOND site (A.6 / rule #11) so a fix-once holds for both,
 * and each call site migrates to these helpers directly — NO delegating shim (A.7).
 *
 * @module a11y-view/sr-shared
 */
/**
 * Collapse every run of whitespace (spaces, tabs, and the embedded newlines the O365
 * `pptx-editor-ir` / `docx-editor-ir` CLIs emit from source paragraph text) into a
 * single space, then trim the ends. This is the "speakability trim" the shared mount
 * deferred (`view-concerns.screenReaderOrder` §4.4): a screen reader announces a
 * paragraph as one utterance, so raw `\n`-laden row text otherwise renders as cramped
 * multi-line runs in the popout. Pure + idempotent; falsy input → `""`.
 */
export function normalizeSpeakableText(text) {
    if (!text)
        return "";
    return String(text).replace(/\s+/g, " ").trim();
}
/**
 * Build the SR-popout close button (the "✕"). Shared DOM so PDF + O365 render an
 * identical control styled by the ONE `.sr-close-btn` rule (`editor.css`). The caller
 * wires its own click handler — the two panels close via different mechanisms (the
 * shared mount's `setSrOpen(false)` vs the classic panel's `close()`) — but the ELEMENT
 * is one. The "✕" glyph is `aria-hidden` so the accessible name is solely the
 * `aria-label` (WCAG 2.5.3 label-in-name — the visible glyph is not in the label).
 */
export function buildSrCloseButton(doc) {
    const btn = doc.createElement("button");
    btn.className = "sr-close-btn editor-btn editor-btn-icon";
    btn.setAttribute("type", "button");
    btn.setAttribute("aria-label", "Close screen reader preview");
    btn.setAttribute("title", "Close (Esc)");
    const icon = doc.createElement("span");
    icon.setAttribute("aria-hidden", "true");
    icon.textContent = "✕";
    btn.appendChild(icon);
    return btn;
}
